import React from 'react';
import { X } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import type { Notification } from '../types/notification';

interface NotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationItem: React.FC<{
  notification: Notification;
  onRead: (id: string) => Promise<void>;
}> = ({ notification, onRead }) => {
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drawer from closing
    
    if (!notification.read) {
      await onRead(notification.id);
    }
    
    // Handle navigation based on notification type
    if (notification.type === 'call_ready' && notification.data?.roomUrl) {
      window.open(notification.data.roomUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      onClick={handleClick}
      className={`
        p-4 border-b cursor-pointer transition-colors
        ${notification.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50 hover:bg-blue-100'}
      `}
    >
      <div className="flex justify-between items-start">
        <div>
          <h4 className="font-medium text-gray-900">{notification.title}</h4>
          <p className="text-sm text-gray-600">{notification.message}</p>
          <span className="text-xs text-gray-500">
            {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
          </span>
        </div>
        {!notification.read && (
          <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
        )}
      </div>
    </div>
  );
};

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const { notifications, markAsRead, markAllAsRead } = useNotifications();

  return (
    <>
    {isOpen && (
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />
    )}
    <div
      className={`
        fixed z-50 inset-y-0 right-0 w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
      `}
    >
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="text-lg font-medium">Notifications</h3>
        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <button 
              onClick={markAllAsRead}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Mark all read
            </button>
          )}
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="overflow-y-auto h-full pb-20">
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No notifications
          </div>
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={markAsRead}
            />
          ))
        )}
      </div>
    </div>
    </>
  );
}; 